import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { useApolloClient } from '@apollo/client/react';
import { ASSETS_QUERY } from '@scio3/graphql-client';
import type { Asset } from '@scio3/shared-types';
import { buildAssetPickerFilter, ASSET_PICKER_TYPES } from '@scio3/shared-types';
import { useTeamStore } from '@/stores/team-store';
import { usePlaylistDialogStore } from '../stores/playlist-dialog-store';
import { LOAD_ALL_LIMIT } from '../utils/playlist-constants';

interface AssetsQueryResult {
  assets: {
    page: {
      edges: Array<{ node: Asset }>;
    };
  };
}

interface UseFolderAddOptions {
  /** Cancel auto-save before opening a dialog. Passed by callers that have
   *  access to the editor auto-save lifecycle (DnD provider, editor panel). */
  cancelAutoSave?: () => void;
}

/**
 * Shared business logic for adding a folder's assets to a playlist.
 * Fetches folder contents, shows empty-folder warning or confirmation dialog.
 * Used by both click (AssetPickerPanel) and DnD (PlaylistDndProvider).
 */
export function useFolderAdd(options?: UseFolderAddOptions) {
  const { t } = useTranslation();
  const apolloClient = useApolloClient();
  const { activeTeamId } = useTeamStore();
  const { openDialog } = usePlaylistDialogStore();
  const cancelAutoSave = options?.cancelAutoSave;

  /**
   * Resolve folder assets and show appropriate dialog.
   * @param folderPath - The folder path to query
   * @param folderName - Display name for the dialog
   * @param onConfirm - Called with resolved asset edges when user confirms
   */
  const resolveFolderAndConfirm = useCallback(
    async (
      folderPath: string,
      folderName: string,
      onConfirm: (edges: Array<{ node: Asset }>) => void,
    ) => {
      try {
        // Apply playlist filter to exclude invalid asset types (audio, QR, emergency apps, etc.)
        const playlistFilter = buildAssetPickerFilter(ASSET_PICKER_TYPES.PLAYLIST);
        const result = await apolloClient.query<AssetsQueryResult>({
          query: ASSETS_QUERY,
          variables: {
            first: LOAD_ALL_LIMIT,
            after: '',
            teamId: activeTeamId,
            query: { path: folderPath, ...playlistFilter },
            sort: { createdAt: 'DESC' },
            extendParams: { isAllItem: true },
          },
          fetchPolicy: 'cache-first',
        });
        const edges = result.data?.assets?.page?.edges ?? [];
        if (edges.length === 0) {
          cancelAutoSave?.();
          openDialog('emptyFolderWarning');
          return;
        }
        cancelAutoSave?.();
        openDialog('confirmFolderDrop', {
          folderDropContext: {
            folderName,
            assetCount: edges.length,
            onConfirm: () => onConfirm(edges),
          },
        });
      } catch {
        toast.error(t('Failed to load folder contents'));
      }
    },
    [apolloClient, activeTeamId, openDialog, cancelAutoSave, t],
  );

  return { resolveFolderAndConfirm };
}
